import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import type { StayFull, StaysListingHead, StaysPolicy } from '../../../../interfaces/stays';
import { getStay, ReservationConflictError, ReservationStaleError, updateStay } from '../../../../api/stays-reservations-client';
import { StaysApiError } from '../../../../api/stays-client';
import { FormModal } from '../../../modal/form-modal';
import { AppButton } from '../../../button/app-button';
import { StatusPill } from '../../../status-pill/status-pill';
import { ClockNote } from '../../../clock-note/clock-note';
import { Field, FieldError, FieldHint, FieldRow, Input } from '../../../form-kit';
import { FormSkeleton, formSkeletonHeight, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { Shake, StateSwitch, StatusLabel } from '../../../../motion';
import toast from '../../../../utils/toast';
import { addDays, todayIn } from '../../../../function-utils/building-time';
import { spacing } from '../../../../style-utils/theme';
import { buildingClock, formatStayDay, isStayDayKey, nightsText } from '../stays-reservations-model';
import {
  amountError,
  applyPatch,
  buildPatch,
  carryEdits,
  changedKeys,
  conflictSentence,
  contextOf,
  FieldErrors,
  firstErrorKey,
  focusField,
  FormKey,
  formFromStay,
  MONEY_KEYS,
  moneyErrors,
  nightsOf,
  StayForm,
  validateForm,
} from './reservation-form-model';
import { describe, errorId, FieldKit, FieldLabel, GuestFields, hintId, NotesFields } from './reservation-fields';
import { MoneyFields } from './money-fields';
import { useAuth } from '../../../../context/auth-provider';
import { isCohost } from '../../../../auth/roles';
import { CleaningFields } from './cleaning-fields';
import {
  AlertBanner,
  FormFieldset,
  FormSection,
  NeutralNotice,
  NudgeInput,
  NudgeRow,
  SectionTitle,
  StaticValue,
} from './reservation-form.styled';

/**
 * EDIT A STAY (ai-plans/stays plan S4, work package S4.9). Opened by the stays
 * table (S4.8) and the calendar (S4.10) with a reservation id; closed with null.
 *
 * It reads the stay itself (GET /stays/reservations/:id), so a row's summary
 * is never what gets edited. Save sends ONLY the fields that changed, with the
 * version read; a STAY_CHANGED answer puts the server's copy underneath, keeps
 * the person's changed fields on top (each tagged "changed") and saves next
 * time with the new version.
 *
 * Which dates may move depends on the stay's status (R-S4.13): confirmed —
 * both; checked in — only check-out, and after the building's today; checked
 * out — only check-out; cancelled / no-show — neither ("Restore the stay
 * before changing its dates."). An archived stay is read-only.
 *
 * Status, archive, access and delete are NOT here: the stays table's row menu
 * (reservations/reservation-actions-menu.tsx) carries them. Its dropdown is
 * absolutely positioned under its trigger and would be clipped by this modal's
 * scrolling body, so it is not mounted in the footer.
 */

export interface EditReservationModalProps {
  /** Open when a reservation id is set. */
  reservationId: string | null;
  listing: StaysListingHead;
  policy?: StaysPolicy;
  /** Open scrolled to a section ("Add amounts" opens at the money). */
  focus?: 'money' | null;
  onClose: () => void;
  /** After any successful write (save, status, archive, access, delete → null). */
  onSaved: (stay: StayFull | null) => void;
}

type Load = 'loading' | 'error' | 'ready';

export const STALE_BANNER = 'Someone else changed this stay. Your edits are kept; review and save again.';

const Nights = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  margin: 0 0 ${spacing.sm};
  min-height: ${spacing.md};
`;

const ClockLine = styled.div`
  margin: 0 0 ${spacing.sm};
`;

export const EditReservationModal: React.FC<EditReservationModalProps> = ({
  reservationId,
  listing,
  policy,
  focus = null,
  onClose,
  onSaved,
}) => {
  const uid = useId();
  const fid = useCallback((key: string) => `${uid}-${key}`, [uid]);
  const formId = fid('edit-stay');
  // IS5: a co-host sees and sends no amounts.
  const hideMoney = isCohost(useAuth().user?.role);
  const ctx = useMemo(() => contextOf(listing, policy, hideMoney), [listing, policy, hideMoney]);

  const [load, setLoad] = useState<Load>('loading');
  const [stay, setStay] = useState<StayFull | null>(null);
  const [buildingToday, setBuildingToday] = useState('');
  const [base, setBase] = useState<StayForm | null>(null);
  const [form, setForm] = useState<StayForm | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [refusal, setRefusal] = useState<string | null>(null);
  const [clash, setClash] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [carried, setCarried] = useState<ReadonlySet<string>>(new Set());
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const readSeq = useRef(0);
  const focusedMoney = useRef(false);

  const adopt = useCallback(
    (full: StayFull & { envelope?: { building_today?: string } }) => {
      const fresh = formFromStay(full, ctx);
      setStay(full);
      setBuildingToday(
        (full.envelope && full.envelope.building_today) || todayIn(full.building_timezone || listing.timezone) || ''
      );
      setBase(fresh);
      setForm(fresh);
    },
    [ctx, listing.timezone]
  );

  const read = useCallback(
    (id: string) => {
      const seq = ++readSeq.current;
      setLoad('loading');
      getStay(id)
        .then(full => {
          if (seq !== readSeq.current) return;
          adopt(full);
          setLoad('ready');
        })
        .catch(() => {
          if (seq === readSeq.current) setLoad('error');
        });
    },
    [adopt]
  );

  /* A new stay opens fresh. Closing (null) keeps what is on screen, so the
     modal's exit animation does not play over an emptied body. */
  useEffect(() => {
    readSeq.current += 1;
    if (!reservationId) return;
    setStay(null);
    setBase(null);
    setForm(null);
    setErrors({});
    setRefusal(null);
    setClash(null);
    setStale(false);
    setCarried(new Set());
    setAttempt(0);
    focusedMoney.current = false;
    read(reservationId);
    // A new stay (or none) only; `read` is stable for a mounted listing.
  }, [reservationId]);

  /* "Add amounts" opens here: scroll to the money and put the cursor in the total.
     StateSwitch mounts the form only after the skeleton has faded out, so the
     fields are not in the DOM yet when load turns 'ready' — retry briefly. */
  useEffect(() => {
    if (load !== 'ready' || focus !== 'money' || focusedMoney.current) return undefined;
    let tries = 0;
    let timer = 0;
    const attempt = () => {
      const gross = document.getElementById(fid('gross_cents'));
      if (!gross) {
        tries += 1;
        if (tries < 30) timer = window.setTimeout(attempt, 50);

        return;
      }
      const section = document.getElementById(fid('money'));
      if (section && typeof section.scrollIntoView === 'function') section.scrollIntoView({ block: 'start' });
      gross.focus();
      focusedMoney.current = true;
    };
    timer = window.setTimeout(attempt, 0);

    return () => window.clearTimeout(timer);
  }, [load, focus, fid]);

  const archived = !!(stay && stay.archived_at);
  const status = stay ? stay.status : 'confirmed';
  const datesAsText = !archived && (status === 'cancelled' || status === 'no_show');
  const checkInLocked = archived || status !== 'confirmed';
  const checkOutLocked = archived || datesAsText;
  const checkOutAfter = status === 'checked_in' ? buildingToday : null;
  const dirty = !!(form && base && changedKeys(form, base, ctx).length > 0);

  const update = (patch: Partial<StayForm>) => {
    const datesMoved = patch.check_in !== undefined || patch.check_out !== undefined;
    if (datesMoved) setClash(null);
    setForm(current => (current ? applyPatch(current, patch, ctx) : current));
    setErrors(current => {
      const next = { ...current };
      for (const key of Object.keys(patch)) delete next[key];
      if (datesMoved) {
        delete next.check_in;
        delete next.check_out;
      }
      if (patch.cleaning_kind !== undefined) {
        delete next.cleaning;
        delete next.cleaning_fee;
      }

      return next;
    });
  };

  const blur = (key: FormKey) => {
    if (!form) return;
    setErrors(current => {
      const next = { ...current };
      if (key === 'cleaning_fee') {
        const problem = form.cleaning_fee.trim() ? amountError(form.cleaning_fee, ctx.currency) : null;
        if (problem) next.cleaning_fee = problem;
        else delete next.cleaning_fee;

        return next;
      }
      const all = moneyErrors(form, ctx);
      if (all[key]) next[key] = all[key];
      else delete next[key];
      for (const k of MONEY_KEYS) if (k !== key && next[k] && !all[k]) delete next[k];

      return next;
    });
  };

  const focusKey = (key: string | null) => {
    if (key) focusField(fid(key));
  };

  /** STAY_CHANGED: the server's copy underneath, the person's edits on top. */
  const reconcile = (current: StayFull, mine: StayForm, keys: FormKey[]) => {
    const fresh = formFromStay(current, ctx);
    setStay(current);
    setBase(fresh);
    setForm(carryEdits(mine, fresh, keys, ctx));
    setCarried(new Set<string>(keys));
  };

  const fail = (error: unknown, mine: StayForm, keys: FormKey[]) => {
    setAttempt(a => a + 1);
    if (error instanceof ReservationStaleError) {
      setStale(true);
      if (error.current) reconcile(error.current, mine, keys);
      else if (stay) {
        getStay(stay.id)
          .then(full => reconcile(full, mine, keys))
          .catch(() => setRefusal('Could not read this stay again. Close it and open it again.'));
      }

      return;
    }
    if (error instanceof StaysApiError && error.status === 400 && Object.keys(error.fields).length) {
      setErrors(error.fields);
      if (!Object.keys(error.fields).some(k => k !== 'version' && k !== 'external_uid')) setRefusal(error.message);
      focusKey(firstErrorKey(error.fields, error.field));

      return;
    }
    if (error instanceof ReservationConflictError) {
      const sentence = conflictSentence(error.code, error.conflict);
      if (sentence) {
        setClash(sentence);
        focusKey(checkInLocked ? 'check_out' : 'check_in');

        return;
      }
    }
    setRefusal(refusalText(error as StaysApiError, 'Could not save the stay. Try again.'));
  };

  const save = async () => {
    if (busy.current || !stay || !form || !base || archived) return;
    setRefusal(null);
    setClash(null);
    setStale(false);
    const keys = changedKeys(form, base, ctx);
    if (!keys.length) {
      onClose();

      return;
    }
    const errs = validateForm(form, ctx, { checkOutAfter: keys.includes('check_out') ? checkOutAfter : null });
    if (Object.keys(errs).length) {
      setErrors(errs);
      setAttempt(a => a + 1);
      focusKey(firstErrorKey(errs));

      return;
    }
    busy.current = true;
    setSaving(true);
    try {
      const next = await updateStay(stay.id, buildPatch(form, base, ctx, stay.version));
      toast.success('Changes saved.');
      onSaved(next);
      onClose();
    } catch (error) {
      fail(error, form, keys);
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  const title = stay
    ? `Edit stay — ${[stay.lead.first_name, stay.lead.last_name].filter(Boolean).join(' ')}`
    : 'Edit stay';

  const footer =
    load === 'ready' && form ? (
      archived ? (
        <AppButton variant="secondary" onClick={onClose}>
          Done
        </AppButton>
      ) : (
        <>
          <AppButton variant="secondary" disabled={saving} onClick={onClose}>
            Cancel
          </AppButton>
          <AppButton type="submit" form={formId} disabled={saving}>
            <StatusLabel state={saving ? 'busy' : 'idle'} labels={{ idle: 'Save changes', busy: 'Saving…' }} />
          </AppButton>
        </>
      )
    ) : (
      <AppButton variant="secondary" onClick={onClose}>
        Cancel
      </AppButton>
    );

  const datesInvalid = !!clash;

  const dateField = (key: 'check_in' | 'check_out', label: string, locked: boolean) => {
    if (!form) return null;
    const value = form[key];
    const valid = isStayDayKey(value);
    const word = key === 'check_in' ? 'check-in' : 'check-out';
    const other = key === 'check_in' ? form.check_out : form.check_in;
    const earlier = valid ? addDays(value, -1) : '';
    const later = valid ? addDays(value, 1) : '';
    const earlierBlocked =
      key === 'check_out' &&
      ((isStayDayKey(other) && earlier <= other) || (!!checkOutAfter && earlier <= checkOutAfter));
    const laterBlocked = key === 'check_in' && isStayDayKey(other) && later >= other;
    const lockReason =
      locked && !archived && key === 'check_in' ? "Check-in can't change once the guest has arrived." : null;
    const hintKey = `${key}-lock`;

    return (
      <Field>
        <FieldLabel kit={kit} field={key} htmlFor={fid(key)}>
          {label}
        </FieldLabel>
        <NudgeRow>
          <AppButton
            variant="ghost"
            size="compact"
            aria-label={`Move ${word} one day earlier`}
            disabled={locked || saving || !valid || earlierBlocked}
            onClick={() => update({ [key]: earlier } as Partial<StayForm>)}
          >
            −1 day
          </AppButton>
          <NudgeInput>
            <Input
              id={fid(key)}
              type="date"
              value={value}
              min={key === 'check_out' && checkOutAfter ? addDays(checkOutAfter, 1) : undefined}
              disabled={locked || saving}
              aria-invalid={errors[key] || datesInvalid ? true : undefined}
              aria-describedby={[describe(kit, key), lockReason ? hintId(kit, hintKey) : null].filter(Boolean).join(' ') || undefined}
              hasError={!!errors[key] || datesInvalid}
              onChange={event => update({ [key]: event.target.value } as Partial<StayForm>)}
            />
          </NudgeInput>
          <AppButton
            variant="ghost"
            size="compact"
            aria-label={`Move ${word} one day later`}
            disabled={locked || saving || !valid || laterBlocked}
            onClick={() => update({ [key]: later } as Partial<StayForm>)}
          >
            +1 day
          </AppButton>
        </NudgeRow>
        {lockReason ? <FieldHint id={hintId(kit, hintKey)}>{lockReason}</FieldHint> : null}
        <FieldError id={errorId(kit, key)} message={errors[key]} />
      </Field>
    );
  };

  const kit: FieldKit = {
    form: form as StayForm,
    errors,
    disabled: saving || archived,
    update,
    blur,
    id: fid,
    changed: carried,
    flat: true,
  };
  const nights = form ? nightsOf(form.check_in, form.check_out) : null;
  const timesHint = `Leave empty to use the listing's ${listing.check_in_time} / ${listing.check_out_time}`;

  return (
    <FormModal
      open={!!reservationId}
      title={title}
      wide
      onClose={onClose}
      isDirty={dirty}
      saving={saving}
      discardMessage="Your changes to this stay have not been saved. Close and lose them?"
      footer={footer}
    >
      <StateSwitch state={load} minHeight={formSkeletonHeight(6)}>
        {load === 'loading' ? (
          <FormSkeleton fields={6} label="stay" />
        ) : load === 'error' ? (
          <LoadError
            message="Couldn't load this stay"
            onRetry={() => {
              if (reservationId) read(reservationId);
            }}
          />
        ) : form && stay ? (
          <Shake active={attempt > 0 && (Object.keys(errors).length > 0 || !!clash || !!refusal || stale)} attempt={attempt}>
            <form
              id={formId}
              noValidate
              aria-label="Edit stay"
              onSubmit={event => {
                event.preventDefault();
                void save();
              }}
            >
              {stale ? (
                <AlertBanner role="alert" data-testid="stay-stale">
                  {STALE_BANNER}
                </AlertBanner>
              ) : null}
              {refusal ? <AlertBanner role="alert">{refusal}</AlertBanner> : null}
              {archived ? (
                <NeutralNotice role="status" data-testid="stay-archived">
                  This stay is archived. Undo the archive first.
                </NeutralNotice>
              ) : null}

              <FormFieldset disabled={archived}>
                <GuestFields kit={kit} maxGuests={listing.max_guests} headingId={fid('h-guest')} />

                <FormSection $flat aria-labelledby={fid('h-dates')}>
                  <SectionTitle id={fid('h-dates')}>Dates</SectionTitle>
                  {clash ? (
                    <AlertBanner role="alert" data-testid="stay-clash">
                      {clash}
                    </AlertBanner>
                  ) : null}
                  {datesAsText ? (
                    <>
                      <StaticValue data-testid="stay-dates-text">
                        {`${formatStayDay(form.check_in)} → ${formatStayDay(form.check_out)}`}
                      </StaticValue>
                      <NeutralNotice role="status">
                        Restore the stay before changing its dates.
                      </NeutralNotice>
                    </>
                  ) : (
                    <FieldRow>
                      {dateField('check_in', 'Check-in', checkInLocked)}
                      {dateField('check_out', 'Check-out', checkOutLocked)}
                    </FieldRow>
                  )}
                  <Nights aria-live="polite">
                    {nights ? (
                      <StatusPill tone="neutral" data-testid="stay-nights">
                        {nightsText(nights)}
                      </StatusPill>
                    ) : null}
                  </Nights>

                  <FieldRow>
                    <Field>
                      <FieldLabel kit={kit} field="expected_arrival_time" htmlFor={fid('expected_arrival_time')}>
                        Expected arrival (optional)
                      </FieldLabel>
                      <Input
                        id={fid('expected_arrival_time')}
                        type="time"
                        value={form.expected_arrival_time}
                        disabled={saving || archived}
                        aria-invalid={errors.expected_arrival_time ? true : undefined}
                        aria-describedby={describe(kit, 'expected_arrival_time') || fid('times-hint')}
                        hasError={!!errors.expected_arrival_time}
                        onChange={event => update({ expected_arrival_time: event.target.value })}
                      />
                      <FieldError id={errorId(kit, 'expected_arrival_time')} message={errors.expected_arrival_time} />
                    </Field>
                    <Field>
                      <FieldLabel kit={kit} field="expected_departure_time" htmlFor={fid('expected_departure_time')}>
                        Expected departure (optional)
                      </FieldLabel>
                      <Input
                        id={fid('expected_departure_time')}
                        type="time"
                        value={form.expected_departure_time}
                        disabled={saving || archived}
                        aria-invalid={errors.expected_departure_time ? true : undefined}
                        aria-describedby={describe(kit, 'expected_departure_time') || fid('times-hint')}
                        hasError={!!errors.expected_departure_time}
                        onChange={event => update({ expected_departure_time: event.target.value })}
                      />
                      <FieldError id={errorId(kit, 'expected_departure_time')} message={errors.expected_departure_time} />
                    </Field>
                  </FieldRow>
                  <FieldHint id={fid('times-hint')}>{timesHint}</FieldHint>
                  <ClockLine>
                    <ClockNote clock={buildingClock(stay.building_timezone || listing.timezone)} />
                  </ClockLine>
                </FormSection>

                {hideMoney ? null : (
                  <MoneyFields kit={kit} ctx={ctx} headingId={fid('h-money')} summary="strip" sectionId={fid('money')} />
                )}
                <CleaningFields kit={kit} ctx={ctx} headingId={fid('h-cleaning')} />
                <NotesFields kit={kit} headingId={fid('h-notes')} />
              </FormFieldset>
            </form>
          </Shake>
        ) : null}
      </StateSwitch>
    </FormModal>
  );
};
